import { useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button, Input, Alert, Card, FormStack } from "@marekl/adminer/components";
import { BrandLockup } from "../../../components/BrandLockup";
import { SupportContacts } from "../../../components/SupportContacts";
import { useResetPassword } from "../../../auth/passwordReset";
import { ApiError } from "../../../lib/api";
import styles from "./ResetPasswordPage.module.css";

/**
 * The page the mailed link opens: set a new password.
 *
 * It does not sign the user in afterwards. Logging in with the password they
 * just chose is what proves it is the one they think it is, and the reset
 * closed every session precisely so that nothing is signed in on the
 * strength of the link alone.
 */
export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";

  const { mutate, isPending, error } = useResetPassword();
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [done, setDone] = useState(false);

  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = again.length > 0 && password !== again;
  const ready = password.length >= 8 && password === again;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutate({ token, password }, { onSuccess: () => setDone(true) });
  }

  function errorMessage(): string | undefined {
    if (!error) return undefined;
    if (error instanceof ApiError) {
      const body = error.body as { message?: string } | null;
      return body?.message ?? "Nastavení hesla selhalo.";
    }
    return "Neočekávaná chyba. Zkuste to prosím znovu.";
  }

  return (
    <div className={styles.page}>
      <Card className={styles.card}>
        <Card.Content>
          <div className={styles.header}>
            <BrandLockup />
            <p className={styles.subtitle}>Nové heslo</p>
          </div>

          {!token ? (
            // Reached without a token: somebody opened the route by hand, or
            // a mail client mangled the link.
            <div className={styles.form}>
              <Alert variant="err">
                Odkaz je neúplný. Otevřete ho prosím přímo z e-mailu, nebo si nechte
                poslat nový.
              </Alert>
              <p className={styles.backLink}>
                <Link to="/obnoveni-hesla">Poslat nový odkaz</Link>
              </p>
            </div>
          ) : done ? (
            <div className={styles.form}>
              <Alert variant="ok">
                Heslo je nastavené. Můžete se znovu přihlásit.
              </Alert>
              <Button variant="primary" block onClick={() => navigate("/login")}>
                Přihlásit se
              </Button>
            </div>
          ) : (
            <FormStack as="form" onSubmit={handleSubmit} noValidate className={styles.form}>
              {errorMessage() && <Alert variant="err">{errorMessage()}</Alert>}

              <Input
                id="password"
                label="Nové heslo"
                type="password"
                autoComplete="new-password"
                autoFocus
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={tooShort ? "Heslo musí mít alespoň 8 znaků" : undefined}
              />

              <Input
                id="password-again"
                label="Heslo znovu"
                type="password"
                autoComplete="new-password"
                required
                value={again}
                onChange={(e) => setAgain(e.target.value)}
                error={mismatch ? "Hesla se neshodují" : undefined}
              />

              <Button
                type="submit"
                variant="primary"
                block
                loading={isPending}
                disabled={!ready}
              >
                Nastavit heslo
              </Button>

              <p className={styles.backLink}>
                <Link to="/login">Zpět na přihlášení</Link>
              </p>
            </FormStack>
          )}
        </Card.Content>
      </Card>
      <SupportContacts />
    </div>
  );
}
